// Penpot's original DOM text editor runs here; only the document adapter and UI are ours.
const MODULE = '/vendor/penpot-text-editor-2.17.2/TextEditor.js';
const ATTRIBUTES = new Set(['bold', 'italic', 'underline', 'strike']);
const node = (tag, text, attributes = {}) => {
  const element = document.createElement(tag);
  if (text) element.textContent = text;
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, value);
  return element;
};

/** Read upstream text spans without storing their DOM in the content document. */
export function readPenpotDelta(root) {
  const delta = [];
  const append = (insert, attributes = {}) => {
    if (!insert) return;
    const previous = delta.at(-1);
    if (previous && JSON.stringify(previous.attributes || {}) === JSON.stringify(attributes)) previous.insert += insert;
    else delta.push(Object.keys(attributes).length ? { insert, attributes } : { insert });
  };
  [...root.children].forEach((paragraph, index) => {
    if (index) append('\n');
    for (const span of paragraph.children) {
      const style = getComputedStyle(span), attributes = {};
      if (Number(style.fontWeight) >= 600 || style.fontWeight === 'bold') attributes.bold = true;
      if (style.fontStyle === 'italic') attributes.italic = true;
      if (style.textDecorationLine.includes('underline')) attributes.underline = true;
      if (style.textDecorationLine.includes('line-through')) attributes.strike = true;
      append(span.textContent, attributes);
    }
  });
  return delta;
}

export function writePenpotDelta(editor, delta, defaults) {
  const paragraphs = []; let spans = [];
  const finish = () => { paragraphs.push(editor.createParagraph(spans.length ? spans : [editor.createTextSpanFromString('', defaults)], defaults)); spans = []; };
  for (const op of delta) {
    if (typeof op.insert !== 'string' || Object.keys(op.attributes || {}).some(key => !ATTRIBUTES.has(key))) {
      throw new Error('이 텍스트의 링크 또는 특수 서식은 캔버스에서 직접 편집해 주세요.');
    }
    const attrs = op.attributes || {};
    const styles = { ...defaults, 'font-weight': attrs.bold ? '700' : '400', 'font-style': attrs.italic ? 'italic' : 'normal',
      'text-decoration': [attrs.underline ? 'underline' : '', attrs.strike ? 'line-through' : ''].filter(Boolean).join(' ') || 'none' };
    op.insert.split('\n').forEach((text, index) => {
      if (index) finish();
      if (text) spans.push(editor.createTextSpanFromString(text, styles));
    });
  }
  finish(); editor.root = editor.createRoot(paragraphs, defaults);
}

export function mountPenpotTypography({ getEditor, getKind, apply, fail }) {
  let instance, selected, disposed = false, opening = false;
  let colorValue = '#17243b';
  let history = [], cursor = -1, pendingHistory;
  const dialog = node('dialog', '', { class: 'es-typography', 'aria-labelledby': 'es-typography-heading' });
  const heading = node('h2', '텍스트 다듬기', { id: 'es-typography-heading' });
  const description = node('p', '내용을 수정하고 서식을 다듬으세요. 서식은 텍스트 전체에 적용됩니다.', { class: 'es-work-hint' });
  let host = node('div', '', { class: 'es-typography-editor', 'aria-label': '텍스트 내용', 'data-testid': 'penpot-text-editor' });
  const toolbar = node('div', '', { class: 'es-typography-toolbar', 'aria-label': '텍스트 서식' });
  const size = node('input', '', { type: 'number', min: '8', max: '200', value: '24', 'aria-label': '글자 크기' });
  const color = node('input', '', { type: 'color', value: '#17243b', 'aria-label': '글자 색' });
  const font = node('select', '', { 'aria-label': '글꼴' });
  for (const [value, label] of [['Arial', '기본 고딕'], ['Content KoPub Dotum', '돋움'], ['Content KoPub Batang', '바탕']]) font.append(node('option', label, { value }));
  const align = node('select', '', { 'aria-label': '텍스트 정렬' });
  for (const [value, label] of [['left', '왼쪽 정렬'], ['center', '가운데 정렬'], ['right', '오른쪽 정렬']]) align.append(node('option', label, { value }));
  const error = node('p', '', { role: 'alert', hidden: '' });
  const button = (text, action, attrs = {}) => {
    const item = node('button', text, { type: 'button', ...attrs });
    item.addEventListener('click', () => { try { action(); } catch (reason) { error.hidden = false; error.textContent = reason.message; } });
    return item;
  };
  const remember = () => {
    clearTimeout(pendingHistory);
    if (!instance) return;
    const value = JSON.stringify({ html: instance.root.innerHTML, size: size.value, color: color.value, colorValue, font: font.value, align: align.value });
    if (history[cursor] === value) return;
    history.splice(cursor + 1); history.push(value);
    if (history.length > 80) history.shift();
    cursor = history.length - 1;
  };
  const restore = direction => {
    remember(); const next = cursor + direction;
    if (next < 0 || next >= history.length) return;
    cursor = next;
    const value = JSON.parse(history[cursor]);
    instance.root.innerHTML = value.html;
    size.value = value.size; color.value = value.color; colorValue = value.colorValue; font.value = value.font; align.value = value.align;
    displayStyle(); instance.focus(); instance.cursorToEnd();
  };
  function bindHost() {
    host.addEventListener('keydown', event => {
      if ((event.ctrlKey || event.metaKey) && ['z', 'y'].includes(event.key.toLowerCase())) {
        event.preventDefault(); event.stopImmediatePropagation(); restore(event.shiftKey || event.key.toLowerCase() === 'y' ? 1 : -1);
      }
    }, true);
    host.addEventListener('input', () => { displayStyle(); pendingHistory = setTimeout(remember, 0); });
  }
  bindHost();
  const styleButton = (label, property, enabled, disabled) => {
    const item = button(label, () => {
      const current = instance.currentStyle.getPropertyValue(property);
      instance.focus(); instance.selectAll();
      instance.applyStylesToSelection({ [property]: current === enabled ? disabled : enabled });
      remember(); instance.focus();
    });
    // Keep the text selection while the formatting button receives pointer input.
    item.addEventListener('pointerdown', event => event.preventDefault());
    return item;
  };
  toolbar.append(button('전체 선택', () => { instance.focus(); instance.selectAll(); }),
    styleButton('굵게', 'font-weight', '700', '400'), styleButton('기울임', 'font-style', 'italic', 'normal'), size, font, color, align);
  function displayStyle() {
    host.style.color = colorValue; host.style.textAlign = align.value;
    for (const element of host.querySelectorAll('[data-itype=paragraph],[data-itype=span]')) {
      element.style.fontSize = `${size.value}px`; element.style.fontFamily = font.value;
      if (element.dataset.itype === 'paragraph') element.style.textAlign = align.value;
    }
  }
  for (const field of [size, font, color, align]) field.addEventListener('input', () => {
    if (field === color) colorValue = color.value;
    displayStyle(); remember();
  });
  function close() {
    dialog.close(); host.blur(); instance?.dispose(); instance = null; clearTimeout(pendingHistory);
    // Upstream 2.17.2 removes capture listeners without their capture flag.
    // Retire the host with the instance so stale callbacks cannot run on reopening.
    const replacement = host.cloneNode(false);
    host.replaceWith(replacement); host = replacement; bindHost();
  }
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  const accept = button('적용', () => {
    if (!size.checkValidity()) throw new Error('글자 크기는 8~200 사이로 입력해 주세요.');
    const delta = readPenpotDelta(instance.root);
    if (!delta.some(op => op.insert.trim())) throw new Error('텍스트를 입력해 주세요.');
    const runs = delta.filter(op => op.insert.trim());
    const bold = Boolean(runs[0].attributes?.bold), italic = Boolean(runs[0].attributes?.italic);
    if (runs.some(op => Boolean(op.attributes?.bold) !== bold || Boolean(op.attributes?.italic) !== italic)) throw new Error('캔버스에서는 텍스트 전체에 같은 굵기와 기울임을 적용해 주세요.');
    const wasBold = selected && (selected.fontWeight === 'bold' || Number(selected.fontWeight) >= 600);
    const wasItalic = selected && ['italic', 'oblique'].includes(selected.fontStyle);
    const properties = { fontSize: Number(size.value), fontFamily: font.value, color: colorValue, textAlign: align.value,
      fontWeight: selected && bold === wasBold ? selected.fontWeight : bold ? '700' : '400',
      fontStyle: selected && italic === wasItalic ? selected.fontStyle : italic ? 'italic' : 'normal' };
    apply(selected?.id, [{ insert: delta.map(op => op.insert).join('') }], properties);
    close();
  }, { class: 'es-work-primary', 'data-testid': 'penpot-apply' });
  const footer = node('div', '', { class: 'es-typography-actions' });
  footer.append(node('a', 'Penpot Text Editor · MPL-2.0', { href: '/vendor/penpot-text-editor-2.17.2/source-manifest.json', target: '_blank', rel: 'noopener' }), button('취소', close), accept);
  dialog.append(heading, description, toolbar, host, error, footer);
  async function open(editSelection) {
    if (disposed || opening || dialog.open) return;
    opening = true;
    try {
      if (getKind() !== 'canvas') throw new Error('이 편집창은 자유 캔버스의 텍스트에 사용합니다. 문서는 본문에서 직접 편집해 주세요.');
      selected = editSelection ? getEditor().getSelectedText() : null;
      if (editSelection && !selected) throw new Error('캔버스에서 텍스트 하나를 선택한 뒤 눌러 주세요.');
      if (selected?.delta.some(op => Object.keys(op.attributes || {}).length)) throw new Error('부분 서식이 있는 텍스트는 캔버스에서 직접 편집해 주세요.');
      const { TextEditor } = await import(MODULE);
      if (disposed) return;
      error.hidden = true; host.replaceChildren();
      size.value = String(selected?.fontSize || 24); colorValue = selected?.color || '#17243b';
      // The picker needs hex, while the source may contain an equivalent rgb() or an alpha color.
      // Retain the original value until the creator explicitly changes the picker.
      const swatch = document.createElement('canvas'); swatch.width = swatch.height = 1;
      const context = swatch.getContext('2d', { willReadFrequently: true });
      context.fillStyle = colorValue; context.fillRect(0, 0, 1, 1);
      color.value = '#' + [...context.getImageData(0, 0, 1, 1).data].slice(0, 3).map(value => value.toString(16).padStart(2, '0')).join('');
      const family = selected?.fontFamily || 'Arial';
      if (![...font.options].some(option => option.value === family)) font.append(node('option', family, { value: family }));
      font.value = family; align.value = selected?.textAlign || 'left';
      const defaults = { 'font-size': Number(size.value), 'font-family': font.value, 'font-weight': '400', 'font-style': 'normal', 'line-height': '1.5', 'text-decoration': 'none', 'text-align': align.value };
      instance = new TextEditor(host, document.createElement('canvas'), { styleDefaults: defaults, allowHTMLPaste: false, shouldUpdatePositionOnScroll: false });
      const delta = selected ? selected.delta.map(op => ({ ...op, attributes: {
        ...(selected.fontWeight === 'bold' || Number(selected.fontWeight) >= 600 ? { bold: true } : {}), ...(['italic', 'oblique'].includes(selected.fontStyle) ? { italic: true } : {}), ...op.attributes,
      } })) : [{ insert: '' }];
      writePenpotDelta(instance, delta, defaults); displayStyle();
      history = []; cursor = -1; remember();
      instance.addEventListener('needslayout', () => { pendingHistory = setTimeout(remember, 0); });
      dialog.showModal(); instance.focus(); instance.cursorToEnd();
    } catch (reason) { close(); fail(reason); }
    finally { opening = false; }
  }
  return { element: dialog, open, dispose() { disposed = true; close(); dialog.remove(); } };
}
